// Explicit 3-choice modal shown when the user tries to delete a recurring
// bill. Replaces the old native `window.confirm` prompt where "Cancel" was
// ambiguously wired to "delete only this instance". Now the wording and
// button intent match exactly.
import { useEffect } from "react";
import { X } from "lucide-react";
import { formatDate } from "@/lib/api";

export function ChooseDeleteScopeModal({ bill, onClose, onChoose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 bg-[#2C302E]/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="delete-scope-modal-backdrop"
    >
      <div
        className="card-soft p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="delete-scope-modal"
      >
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[#7A2E24]">Delete recurring bill</div>
            <h2 className="font-heading text-xl text-[#2C302E] mt-1">{bill.name}</h2>
            <p className="text-xs text-[#59605C] mt-2 leading-relaxed">
              This bill is part of a recurring series. Choose whether you want to remove
              just this one occurrence (due {formatDate(bill.due_date)}) or every past and
              future instance in the series.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[#8A918D] hover:text-[#2C302E] shrink-0"
            data-testid="delete-scope-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="space-y-2 mt-4">
          <button
            type="button"
            onClick={() => onChoose("instance")}
            className="w-full text-left p-3 rounded-xl border border-[#E5B4A3] bg-[#F5D9D1] hover:brightness-95 transition-all"
            data-testid="delete-scope-instance"
          >
            <div className="text-sm font-semibold text-[#7A2E24]">Delete this occurrence only</div>
            <div className="text-xs text-[#8C3A30] mt-0.5">
              Just remove the {formatDate(bill.due_date)} instance. Future months keep coming as scheduled.
            </div>
          </button>
          <button
            type="button"
            onClick={() => onChoose("series")}
            className="w-full text-left p-3 rounded-xl border border-[#8C3A30] bg-[#7A2E24] text-white hover:brightness-105 transition-all"
            data-testid="delete-scope-series"
          >
            <div className="text-sm font-semibold">Delete the entire recurring series</div>
            <div className="text-xs opacity-90 mt-0.5">
              Removes this bill AND every past/future occurrence. Cannot be fully undone from a toast.
            </div>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full text-center px-3 py-2 rounded-xl text-sm text-[#59605C] hover:bg-[#F1EEE7] transition-all"
            data-testid="delete-scope-cancel"
          >
            Cancel — keep the bill
          </button>
        </div>
      </div>
    </div>
  );
}
